import { Box, Button, IconButton, Popover, TextField } from "@mui/material";
import * as React from "react";
import { ja } from "date-fns/locale";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import styled from "styled-components";
import date_picker_icon from "@assets/Icons/date_picker_icon.svg";

const StyledAppSingleFieldRangeDatePicker = styled.div`
  .datePicker {
    position: relative;
    width: 100%;
  }

  .datePicker input {
    width: 100%;
    padding: 4px 12px;
    font-size: 14px;
    cursor: pointer;
  }
}

  .datePickerPopup {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    background: white;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    margin-top: 4px;
    border: 1px solid #ccc;
    padding: 10px;
  }

  .calendarsContainer {
    display: flex;
    gap: 16px;
  }

  .calendarWrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .calendarHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 8px;
  }

  .calendarLabel {
    font-size: 14px;
    color: #666;
  }

  .selectedDate {
    font-size: 14px;
    color: #333;
    margin-left: 8px;
  }

  .clearButton {
    font-size: 13px;
    padding: 4px 8px;
    min-width: auto;
  }

  .disabled-date {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
    pointer-events: none;
  }

  .react-date-range .rdrDayDisabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }

`;

interface IProps {
  value: { startDate: Date | null; endDate: Date | null };
  onChange: (dateRange: { startDate: Date | null; endDate: Date | null }) => void;
  placeholder?: string;
}

export const AppSingleFieldRangeDatePicker = (props: IProps) => {
  const { value, onChange, placeholder } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const formatDateRange = (startDate: Date | null, endDate: Date | null): string => {
    if (!startDate && !endDate) return '';
    
    const formatDate = (date: Date | null): string => {
      if (!date) return '';
      return `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
    };

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    if (start && end) return `${start} ～ ${end}`;
    if (start) return `${start} ～`;
    if (end) return `～ ${end}`;
    return '';
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
  };

  const handleStartDateChange = (date: Date) => {
    if (value.endDate && date > value.endDate) {
      onChange({
        startDate: date,
        endDate: null,
      });
      return;
    }
    onChange({
      startDate: date,
      endDate: value.endDate,
    });
  };

  const handleEndDateChange = (date: Date) => {
    if (value.startDate && date < value.startDate) {
      return;
    }
    onChange({
      startDate: value.startDate,
      endDate: date,
    });
  };

  const handleClearStartDate = () => {
    onChange({
      startDate: null,
      endDate: value.endDate,
    });
  };

  const handleClearEndDate = () => {
    onChange({
      startDate: value.startDate,
      endDate: null,
    });
  };

  const getDisabledStartDates = (): Date[] => {
    if (!value.endDate) return [];
    const disabledDates: Date[] = [];
    const endDate = new Date(value.endDate);
    const currentDate = new Date(endDate);
    currentDate.setDate(currentDate.getDate() + 1);
    
    // 終了日より後の日付を無効化
    while (currentDate <= new Date(2100, 11, 31)) { // 2100年12月31日までの日付を制限
      disabledDates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return disabledDates;
  };

  const getDisabledEndDates = (): Date[] => {
    if (!value.startDate) return [];
    const disabledDates: Date[] = [];
    const startDate = new Date(value.startDate);
    const currentDate = new Date(startDate);
    currentDate.setDate(currentDate.getDate() - 1);
    
    // 開始日より前の日付を無効化
    while (currentDate >= new Date(2000, 0, 1)) { // 2000年1月1日以降の日付を制限
      disabledDates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() - 1);
    }
    
    return disabledDates;
  };

  return (
    <StyledAppSingleFieldRangeDatePicker>
      <div className="datePicker" ref={anchorRef}>
        <TextField
          fullWidth
          value={formatDateRange(value.startDate, value.endDate)}
          placeholder={placeholder}
          onClick={() => setIsOpen(true)}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton>
                <img src={date_picker_icon} alt="icon" width={20} height={20}/>
              </IconButton>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              padding:"0px",
              "& fieldset": {
                border: "none"
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
        <Popover
          open={isOpen}
          anchorEl={anchorRef.current}
          onClose={() => setIsOpen(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 2 }}>
            <div className="calendarsContainer">
              <div className="calendarWrapper">
                <div className="calendarHeader">
                  <div style={{ fontSize:'14px'}}>
                    <span className="calendarLabel">開始日: </span>
                    {value.startDate && (
                      <span className="selectedDate"><strong>{formatDate(value.startDate)}</strong></span>
                    )}
                  </div>
                  {value.startDate && (
                    <Button
                      className="clearButton"
                      variant="outlined"
                      size="medium"
                      sx={{ fontSize: '14px', paddingTop:'1px', paddingBottom:'1px', marginTop:'3px' }}
                      onClick={handleClearStartDate}
                    >
                      クリア
                    </Button>
                  )}
                </div>
                <Calendar
                  date={value.startDate || new Date()}
                  onChange={handleStartDateChange}
                  locale={ja}
                  disabledDates={getDisabledStartDates()}
                />
              </div>
              <div className="calendarWrapper">
                <div className="calendarHeader">
                  <div style={{ fontSize:'14px'}}>
                    <span className="calendarLabel">終了日: </span>
                    {value.endDate && (
                      <span className="selectedDate"><strong>{formatDate(value.endDate)}</strong></span>
                    )}
                  </div>
                  {value.endDate && (
                    <Button
                      className="clearButton"
                      variant="outlined"
                      size="medium"
                      sx={{ fontSize: '14px', paddingTop:'1px', paddingBottom:'1px', marginTop:'3px' }}
                      onClick={handleClearEndDate}
                    >
                      クリア
                    </Button>
                  )}
                </div>
                <Calendar
                  date={value.endDate || new Date()}
                  onChange={handleEndDateChange}
                  locale={ja}
                  disabledDates={getDisabledEndDates()}
                />
              </div>
            </div>
          </Box>
        </Popover>
      </div>
    </StyledAppSingleFieldRangeDatePicker>
  );
};

export default AppSingleFieldRangeDatePicker; 